import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig } from '@angular/core';
import {
    DateAdapter,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
} from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideApi } from './core/api/api.provider';
import { provideCache } from './core/cache/cache.provider';
import { provideMapWithPlaces } from './core/map-loader/map-loader.provider';
import { provideSocket } from './core/socket/socket.provider';
import { CustomLuxonDateAdapter } from './helpers/CustomDateAdapter';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(),
        provideEnvironmentNgxMask(),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: CustomLuxonDateAdapter,
            deps: [MAT_DATE_LOCALE],
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'MMM d, yyyy', // "Aug 1, 2025"
                    monthYearLabel: 'yyyy', // "2025"
                    dateA11yLabel: 'MMMM d, yyyy', // accessibility: "August 1, 2025"
                    monthYearA11yLabel: 'MMMM yyyy', // accessibility: "August 2025"
                },
            },
        },

        // Fuse
        provideAuth(),
        provideApi(),
        provideCache(),
        provideMapWithPlaces(),
        provideSocket(),
        provideIcons(),
        provideFuse({
            fuse: {
                layout: 'futuristic',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-dealflow',
                themes: [
                    {
                        id: 'theme-dealflow',
                        name: 'DealFlow',
                    },
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
/**
 * Navigation Type
 *
 * @defaultValue Static
 */
export const navigationType: 'Static' | 'Dynamic' = 'Dynamic';

/**
 * App Name
 */
export const appName = 'Deal Flow CRM';

/**
 * Pagination Limit
 *
 * @defaultValue 10
 */
export const paginationLimit = 10;

/**
 * Password Strength Settings
 *
 * @defaultValue Low
 */
export const passwordStrength: 'Low' | 'High' = 'Low';

/**
 * Enable Socket.IO
 *
 * @defaultValue false
 */
export const enableSocketIO = false;
